import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import CTABanner from "@/components/CTABanner";
import { Reveal, Eyebrow, MaskLines } from "@/components/Reveal";

const IMG_REFINERY = "https://images.unsplash.com/photo-1629447388369-760612337eff?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxvaWwlMjBnYXMlMjBwaXBlbGluZSUyMHJlZmluZXJ5JTIwbmlnaHQlMjBpbmR1c3RyaWFsJTIwaW5mcmFzdHJ1Y3R1cmV8ZW58MHx8fHwxNzg5NTA1NDMxfDA&ixlib=rb-4.1.0&q=85";
const IMG_POWER = "https://images.unsplash.com/photo-1509390288171-ce2088f7d08e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxvaWwlMjBnYXMlMjBwaXBlbGluZSUyMHJlZmluZXJ5JTIwbmlnaHQlMjBpbmR1c3RyaWFsJTIwaW5mcmFzdHJ1Y3R1cmV8ZW58MHx8fHwxNzg5NTA1NDMxfDA&ixlib=rb-4.1.0&q=85";
const IMG_WELD = "https://images.unsplash.com/photo-1684594943477-1c52c60d92a9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxpbmR1c3RyaWFsJTIwaW5zcGVjdG9yJTIwcGlwZWxpbmUlMjByZWZpbmVyeSUyMHdlbGRpbmclMjBoaWdoJTIwcXVhbGl0eXxlbnwwfHx8fDE3ODk1MDU0MjV8MA&ixlib=rb-4.1.0&q=85";
const IMG_INSPECTOR = "https://images.unsplash.com/photo-1507497806295-753c4108560c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwaW5zcGVjdG9yJTIwcGlwZWxpbmUlMjByZWZpbmVyeSUyMHdlbGRpbmclMjBoaWdoJTIwcXVhbGl0eXxlbnwwfHx8fDE3ODk1MDU0MjV8MA&ixlib=rb-4.1.0&q=85";
const IMG_PIPELINE = "https://images.unsplash.com/photo-1716031112197-db7e3e1e232e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwaW5zcGVjdG9yJTIwcGlwZWxpbmUlMjByZWZpbmVyeSUyMHdlbGRpbmclMjBoaWdoJTIwcXVhbGl0eXxlbnwwfHx8fDE3ODk1MDU0MjV8MA&ixlib=rb-4.1.0&q=85";

const sectors = [
  {
    num: "01",
    name: "Industrial Facilities",
    img: IMG_REFINERY,
    desc: "Refineries, chemical plants, and processing facilities run on verified integrity. APX provides NDE auditing, in-service inspection support, and QA/QC oversight that keeps assets compliant and operating.",
    caps: ["In-service & outage inspection support", "NDE program audits & procedure verification", "Fixed equipment quality documentation"],
  },
  {
    num: "02",
    name: "Pipeline Operations",
    img: IMG_PIPELINE,
    desc: "From new construction spreads to integrity digs, pipeline work demands fast, accurate, code-tight inspection. APX delivers Level III oversight, film auditing, and field reporting along the right-of-way.",
    caps: ["Mainline & tie-in weld inspection oversight", "Radiographic film auditing & second reads", "Contractor NDE performance audits"],
  },
  {
    num: "03",
    name: "Utility Infrastructure",
    img: IMG_POWER,
    desc: "Power generation, transmission, and distribution assets carry public consequence. APX supports utilities with disciplined inspection, documentation review, and compliance verification.",
    caps: ["Generation asset NDE support", "Weld & bolting quality verification", "Regulatory documentation readiness"],
  },
  {
    num: "04",
    name: "Energy & Construction Projects",
    img: IMG_WELD,
    desc: "Capital projects live or die on quality execution. APX embeds with project teams to audit contractors, verify procedures, and keep quality documentation moving at construction speed.",
    caps: ["Owner-side client representation", "Contractor oversight & surveillance", "Turnover package & records review"],
  },
  {
    num: "05",
    name: "Maintenance & Turnaround Operations",
    img: IMG_INSPECTOR,
    desc: "Turnarounds compress months of inspection into weeks. APX brings rapid mobilization, clear field reporting, and zero-defect discipline to the most schedule-critical work in the industry.",
    caps: ["TAR inspection planning & execution support", "Safety & quality monitoring on site", "Daily field reporting & NCR tracking"],
  },
];

export default function Industries() {
  useEffect(() => {
    document.title = "Industries | APX Project Solutions LLC";
  }, []);

  return (
    <div data-testid="industries-page">
      <section className="tech-grid relative overflow-hidden border-b border-white/[0.08]" data-testid="industries-hero">
        <div className="mx-auto max-w-7xl px-5 py-24 sm:px-8 sm:py-32">
          <Eyebrow>Industries Served</Eyebrow>
          <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.92] tracking-tight text-white sm:text-7xl lg:text-8xl">
            <MaskLines lines={[<>Built for the</>, <span className="text-apx">critical sectors.</span>]} />
          </h1>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-zinc-400 sm:text-lg">
            Five sectors. One expectation: inspection and quality work that never becomes the reason a project stops.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-28" data-testid="industries-list">
        <div className="space-y-20">
          {sectors.map((s, i) => (
            <Reveal key={s.num}>
              <article
                className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
                data-testid={`sector-${s.num}`}
              >
                <div className="chamfer group relative overflow-hidden border border-white/10">
                  <img src={s.img} alt={s.name} className="h-[340px] w-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
                  <span className="absolute left-5 top-5 font-display text-6xl font-black text-white/20">{s.num}</span>
                </div>
                <div>
                  <span className="font-mono text-xs uppercase tracking-[0.25em] text-apx">Sector {s.num}</span>
                  <h2 className="mt-4 font-display text-3xl font-black uppercase leading-none tracking-tight text-white sm:text-5xl">{s.name}</h2>
                  <p className="mt-5 max-w-lg text-sm leading-relaxed text-zinc-400 sm:text-base">{s.desc}</p>
                  <ul className="mt-7 space-y-3 border-t border-white/[0.08] pt-6">
                    {s.caps.map((c) => (
                      <li key={c} className="flex items-start gap-3 text-sm text-zinc-300">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-apx" />
                        {c}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.08] bg-coal py-20" data-testid="industries-cta-strip">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-5 sm:px-8 lg:flex-row lg:items-center">
          <Reveal>
            <h2 className="max-w-xl font-display text-3xl font-black uppercase leading-tight tracking-tight text-white sm:text-4xl">
              Your sector, your spec, our standard.
            </h2>
          </Reveal>
          <Link
            to="/contact"
            data-testid="industries-contact-link"
            className="chamfer-sm group inline-flex items-center gap-2 bg-apx px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-ink transition-colors hover:bg-white"
          >
            Discuss Your Project <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
